import { XrayJiraIssueData } from '../xray/xray.reference';
import { Reference, ReferenceProperty } from './Reference.interface';

// common

const User: Reference = {
  name: 'User',
  typeName: 'User',
  props: [
    { id: 'id', name: 'ID', type: 'number' },
    { id: 'firstName', name: 'First Name', type: 'string' },
    { id: 'lastName', name: 'Last Name', type: 'string' },
    { id: 'email', name: 'Email', type: 'string' }
  ]
};

const Product: Reference = {
  name: 'Product',
  typeName: 'Product',
  props: [
    { id: 'name', name: 'Product Name', type: 'string' },
    { id: 'description', name: 'Product Description', type: 'string' },
    { id: 'intendedUse', name: 'Intended Use', type: 'string' },
    {
      id: 'version',
      name: 'Version',
      type: 'string',
      description: 'The Product version.'
    }
  ]
};

const TestStep: Reference = {
  name: 'Test Step',
  typeName: 'TestStep',
  props: [
    { name: 'Step Number', id: 'stepNo', type: 'string' },
    { name: 'Description', id: 'description', type: 'string' },
    { name: 'Sample Data', id: 'sampleData', type: 'string' },
    { name: 'Expected Result', id: 'expectedResult', type: 'string' }
  ]
};

const LinkedJiraIssues: Reference = {
  name: 'Linked Jira Issues',
  typeName: 'LinkedJiraIssues',
  props: [
    { name: 'Jira Issue Key', id: 'key', type: 'string' },
    { name: 'Jira Issue ID', id: 'id', type: 'string' },
    { name: 'Jira Issue Summary', id: 'summary', type: 'string' },
    { name: 'Jira Issue Status', id: 'status', type: 'string' },
    {
      name: 'Jira Issue Description',
      id: 'issue.fields.description',
      type: 'string'
    },
    {
      name: 'Jira Issue Field or Custom Field Value',
      id: 'issue.fields.[field_name]',
      type: 'string',
      description: 'e.g. issue.fields.summary, issue.fields.customfield_10001'
    },
    {
      name: 'Jira Issue Select Field Value',
      id: 'issue.fields.[field_name].value',
      type: 'string',
      description: 'e.g. issue.fields.customfield_10001.value'
    },
    {
      id: 'xray',
      name: 'Jira Issue Xray Data',
      description: 'Xray Data for this Jira issue if available',
      type: XrayJiraIssueData
    }
  ]
};

const RiskTable: Reference = {
  name: 'Risk Table',
  typeName: 'RiskTable',
  props: [
    { id: 'id', name: 'Risk ID', type: 'string' },
    { id: 'title', name: 'Risk Title', type: 'string' },
    { id: 'relatedHazard', name: 'Related Hazard', type: 'string' },
    { id: 'hazardSituation', name: 'Hazard Situation', type: 'string' },
    { id: 'potentialHarm', name: 'Potential Harm', type: 'string' },
    { id: 'potentialCauses', name: 'Potential Causes', type: 'string' },
    { id: 'relevance', name: 'Components Name', type: 'string' },
    { id: 'currentControls', name: 'Current Controls', type: 'string' },
    { id: 'recommendedActions', name: 'Recommended Actions', type: 'string' },
    { id: 'actionsTaken', name: 'Actions Taken', type: 'string' },
    { id: 'verification', name: 'Verification', type: 'string' },
    { id: 'p1', name: 'P1 Value', type: 'number' },
    { id: 'o1', name: 'O1 Value', type: 'number' },
    { id: 's1', name: 'S1 Value', type: 'number' },
    { id: 'd1', name: 'D1 Value', type: 'number' },
    { id: 'p2', name: 'P2 Value', type: 'number' },
    { id: 'o2', name: 'O2 Value', type: 'number' },
    { id: 's2', name: 'S2 Value', type: 'number' },
    { id: 'd2', name: 'D2 Value', type: 'number' },
    { id: 'rpn1', name: 'RPN1 Value', type: 'number' },
    { id: 'r1', name: 'R1 Value', type: 'number' },
    { id: 'rpn2', name: 'RPN1 Value', type: 'number' },
    { id: 'r2', name: 'R2 Value', type: 'number' }
  ]
};

// user need
const UserNeed: Reference = {
  name: 'User Need',
  typeName: 'UserNeed',
  props: [
    { id: 'id', name: 'User Need ID', type: 'string' },
    { id: 'title', name: 'User Need Title', type: 'string' },
    { id: 'description', name: 'User Need Description', type: 'string' },
    {
      id: 'linkedJiraIssues',
      name: 'User Need Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { id: 'risk', name: 'User Need Risk', type: 'string' }
  ]
};

const UserNeedTableRow: Reference = {
  name: 'User Need Table Row',
  typeName: 'UserNeedTableRow',
  props: [
    { id: 'unId', name: 'User Need ID', type: 'string' },
    { id: 'unTitle', name: 'User Need Title', type: 'string' },
    { id: 'unDescription', name: 'User Need Description', type: 'string' },
    {
      id: 'unJiraIssues',
      name: 'User Need Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { id: 'risk', name: 'User Need Risk', type: 'string' }
  ]
};

const UserNeedTable: Reference = {
  name: 'User Need Table',
  typeName: 'UserNeedTable',
  props: [
    { id: 'id', name: 'Component ID', type: 'string' },
    { id: 'component', name: 'Component Name', type: 'string' },
    {
      id: 'rows',
      name: 'User Need Table Row',
      type: UserNeedTableRow,
      isArray: true
    }
  ]
};

const UserNeedRiskTable: Reference = {
  name: 'User Need Risk Table',
  typeName: 'UserNeedRiskTable',
  props: [
    { id: 'unId', name: 'User Need ID', type: 'string' },
    { id: 'unTitle', name: 'User Need Title', type: 'string' },
    { id: 'unDescription', name: 'User Need Description', type: 'string' },
    {
      id: 'unJiraIssues',
      name: 'User Need Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    ...RiskTable.props
  ]
};

export const UserNeedReference: Reference = {
  name: 'User Need Template Reference',
  typeName: 'UserNeedTemplateReference',
  description: 'Template Reference for User Need document export.',
  props: [
    { id: 'product', name: 'Product', type: Product },
    {
      id: 'version',
      type: 'string',
      name: 'Document Version',
      description: 'The version of the document.'
    },
    { id: 'userNeeds', name: 'User Needs', type: UserNeed, isArray: true },
    {
      id: 'table',
      name: 'User Need Table',
      type: UserNeedTable,
      isArray: true
    },
    {
      id: 'riskTable',
      name: 'User Need Risk Table',
      type: UserNeedRiskTable,
      isArray: true
    }
  ]
};

// Design Input
const DesignInput: Reference = {
  name: 'Design Input',
  typeName: 'DesignInput',
  props: [
    { id: 'id', name: 'Design Input ID', type: 'string' },
    { id: 'title', name: 'Design Input Title', type: 'string' },
    { id: 'description', name: 'Design Input Description', type: 'string' },
    {
      id: 'linkedJiraIssues',
      name: 'Design Input Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};

const DesignInputTableRow: Reference = {
  name: 'Design Input Table Row',
  typeName: 'DesignInputTableRow',
  props: [
    { id: 'inId', name: 'Design Input ID', type: 'string' },
    { id: 'inTitle', name: 'Design Input Title', type: 'string' },
    { id: 'inDescription', name: 'Design Input Description', type: 'string' },
    {
      id: 'inJiraIssues',
      name: 'Design Input Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { id: 'risk', name: 'User Need Risk', type: 'string' },
    { id: 'unId', name: 'User Need ID', type: 'string' },
    { id: 'unTitle', name: 'User Need Title', type: 'string' },
    { id: 'unDescription', name: 'User Need Description', type: 'string' },
    {
      id: 'unJiraIssues',
      name: 'User Need Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { id: 'inComponent', name: 'Design Input Component', type: 'string' },
    {
      id: 'inEssentialRequirements',
      name: 'User Need Essential Requirements',
      type: 'string'
    },
    {
      id: 'inVerificationMethod',
      name: 'User Need Verification Method',
      type: 'string'
    },
    { id: 'inWhenToVerify', name: 'User Need When to Verify', type: 'string' },
    { id: 'verId', name: 'Verification ID', type: 'string' },
    { id: 'verTitle', name: 'Verification Title', type: 'string' },
    { id: 'verDescription', name: 'Verification Description', type: 'string' },
    {
      id: 'verJiraIssues',
      name: 'Verification Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { id: 'valId', name: 'Validation ID', type: 'string' },
    { id: 'valTitle', name: 'Validation Title', type: 'string' },
    { id: 'valDescription', name: 'Validation Description', type: 'string' },
    {
      id: 'valJiraIssues',
      name: 'Validation Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};

const DesignInputTable: Reference = {
  name: 'Design Input Table',
  typeName: 'DesignInputTable',
  props: [
    { id: 'component', name: 'Product Component', type: 'string' },
    {
      id: 'rows',
      name: 'Design Input Table Row',
      type: DesignInputTableRow,
      isArray: true
    }
  ]
};

const DesignInputRiskTable: Reference = {
  name: 'Design Input Risk Table',
  typeName: 'DesignInputRiskTable',
  props: [
    { id: 'inId', name: 'Design Input ID', type: 'string' },
    { id: 'inTitle', name: 'Design Input Title', type: 'string' },
    { id: 'inDescription', name: 'Design Input Description', type: 'string' },
    {
      id: 'inJiraIssues',
      name: 'Design Input Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    ...RiskTable.props
  ]
};
export const DesignInputReference: Reference = {
  name: 'Design Input Template Reference',
  typeName: 'DesignInputTemplateReference',
  description: 'Template Reference for Design Input document export.',
  props: [
    { id: 'product', name: 'Product', type: Product },
    {
      id: 'version',
      type: 'string',
      name: 'Document Version',
      description: 'The version of the document.'
    },
    {
      id: 'designInputs',
      name: 'Design Inputs',
      type: DesignInput,
      isArray: true
    },
    {
      id: 'table',
      name: 'Design Input Table',
      type: DesignInputTable,
      isArray: true
    },
    {
      id: 'riskTable',
      name: 'Design Input Risk Table',
      type: DesignInputRiskTable,
      isArray: true
    }
  ]
};

// Design Output
const DesignOutputs: Reference = {
  name: 'Design Outputs',
  typeName: 'DesignOutputs',
  props: [
    { name: 'Index', id: 'index', type: 'number' },
    { name: 'ID', id: 'id', type: 'string' },
    { name: 'Description', id: 'description', type: 'string' },
    {
      name: 'Linked Jira Issues',
      id: 'linkedJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    },
    {
      name: 'Essential Requirements',
      id: 'essentialRequirements',
      type: 'string'
    },
    { name: 'Drawing', id: 'drawing', type: 'string' },
    { name: 'Components', id: 'components', type: 'string' },
    { name: 'Related Hazard', id: 'relatedHazard', type: 'string' },
    { name: 'Hazard Situation', id: 'hazardSituation', type: 'string' },
    { name: 'Potential Harm', id: 'potentialHarm', type: 'string' },
    { name: 'Potential Causes', id: 'potentialCauses', type: 'string' },
    { name: 'Current Controls', id: 'currentControls', type: 'string' },
    { name: 'Relevance', id: 'relevance', type: 'string' },
    {
      name: 'Recommended Actions/Mitigations',
      id: 'recommendedActions',
      type: 'string'
    },
    { name: 'Actions Taken', id: 'actionsTaken', type: 'string' },
    { name: 'Verification', id: 'verification', type: 'string' },
    { name: 'D1', id: 'd1', type: 'number' },
    { name: 'D2', id: 'd2', type: 'number' },
    { name: 'P1', id: 'p1', type: 'number' },
    { name: 'P2', id: 'p2', type: 'number' },
    { name: 'RPN1', id: 'rpn1', type: 'number' },
    { name: 'RPN2', id: 'rpn2', type: 'number' },
    { name: 'S1', id: 's1', type: 'number' },
    { name: 'S2', id: 's2', type: 'number' }
  ]
};

const DesignOutputTable: Reference = {
  name: 'Design Output Table',
  typeName: 'DesignOutputTable',
  props: [
    { name: 'Design Input ID', id: 'inId', type: 'string' },
    { name: 'Design Input Title', id: 'inTitle', type: 'string' },
    {
      name: 'Design Input Essential Requirements',
      id: 'inEssentialRequirements',
      type: 'string'
    },
    {
      name: 'Design Input Verification Method',
      id: 'inVerificationMethod',
      type: 'string'
    },
    {
      name: 'Design Input When to Verify',
      id: 'inWhenToVerify',
      type: 'string'
    },
    {
      name: 'Design Input Linked Jira Issues',
      id: 'inJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { name: 'Design Output ID', id: 'outId', type: 'string' },
    { name: 'Design Output Title', id: 'outTitle', type: 'string' },
    {
      name: 'Design Output EssentialRequirements',
      id: 'outEssentialRequirements',
      type: 'string'
    },
    {
      name: 'Design Output Linked Jira Issues',
      id: 'outJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { name: 'Verification ID', id: 'verId', type: 'string' },
    { name: 'Verification Title', id: 'verTitle', type: 'string' },
    {
      name: 'Verification Linked Jira Issues',
      id: 'verJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};
const DesignOutputRiskTable: Reference = {
  name: 'Design Output Risk Table',
  typeName: 'DesignOutputRiskTable',
  props: [
    { name: 'Design Output ID', id: 'outId', type: 'string' },
    { name: 'Design Output Title', id: 'outTitle', type: 'string' },
    {
      name: 'Design Output Linked Jira Issues',
      id: 'outJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    },
    ...RiskTable.props
  ]
};

export const DesignOutputReference: Reference = {
  name: 'Design Output Template Reference',
  typeName: 'DesignOutputTemplateReference',
  props: [
    {
      name: 'Product',
      id: 'product',
      type: Product
    },
    { name: 'Version', id: 'version', type: 'string' },
    {
      name: 'Design Outputs',
      id: 'designOutputs',
      type: DesignOutputs,
      isArray: true
    },
    {
      name: 'Design Output Table',
      id: 'table',
      type: DesignOutputTable,
      isArray: true
    },
    {
      name: 'Design Output Risk Table',
      id: 'riskTable',
      type: DesignOutputRiskTable,
      isArray: true
    }
  ]
};

// Verification

const Verifications: Reference = {
  name: 'Verifications',
  typeName: 'Verifications',
  props: [
    { name: 'ID', id: 'id', type: 'number' },
    { name: 'Title', id: 'title', type: 'string' },
    { name: 'Description', id: 'description', type: 'string' },
    { name: 'Test Step', id: 'testStep', type: TestStep, isArray: true },
    {
      name: 'Linked Jira Issues',
      id: 'linkedJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};

const VerificationComponents: Reference = {
  name: 'Verification Components',
  typeName: 'Verification Components',
  props: [
    { name: 'Index', id: 'index', type: 'number' },
    { name: 'Name', id: 'name', type: 'string' },
    {
      name: 'Verifications',
      id: 'verifications',
      type: Verifications,
      isArray: true
    }
  ]
};

const VerificationWithoutComponents: Reference = {
  name: 'Without Components',
  typeName: 'WithoutComponents',
  props: [
    { name: 'ID', id: 'id', type: 'number' },
    { name: 'Title', id: 'title', type: 'number' },
    { name: 'Description', id: 'description', type: 'string' },
    { name: 'Test Step', id: 'testStep', type: TestStep, isArray: true },
    {
      name: 'Linked Jira Issues',
      id: 'linkedJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};

export const VerificationReference: Reference = {
  name: 'Verification Template Reference',
  typeName: 'VerificationTemplateReference',
  props: [
    {
      name: 'Product',
      id: 'product',
      type: Product
    },
    { name: 'Version', id: 'version', type: 'number' },
    {
      name: 'Components',
      id: 'components',
      type: VerificationComponents,
      isArray: true
    },
    { name: 'Total', id: 'total', type: 'number' },
    {
      name: 'Verification Without Components',
      id: 'verificationWithoutComponents',
      type: VerificationWithoutComponents,
      isArray: true
    }
  ]
};

// Validation

const Validations: Reference = {
  name: 'Validations',
  typeName: 'Validations',
  props: [
    { name: 'ID', id: 'id', type: 'number' },
    { name: 'Title', id: 'title', type: 'string' },
    { name: 'Description', id: 'description', type: 'string' },
    { name: 'Test Step', id: 'testStep', type: TestStep, isArray: true },
    {
      name: 'Linked Jira Issues',
      id: 'linkedJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};

const ValidationComponents: Reference = {
  name: 'Validations Components',
  typeName: 'Validations Components',
  props: [
    { name: 'Index', id: 'index', type: 'number' },
    { name: 'Name', id: 'name', type: 'string' },
    {
      name: 'Validations',
      id: 'validations',
      type: Validations,
      isArray: true
    }
  ]
};

const ValidationWithoutComponents: Reference = {
  name: 'Without Components',
  typeName: 'WithoutComponents',
  props: [
    { name: 'ID', id: 'id', type: 'number' },
    { name: 'Title', id: 'title', type: 'number' },
    { name: 'Description', id: 'description', type: 'string' },
    { name: 'Test Step', id: 'testStep', type: TestStep, isArray: true },
    {
      name: 'Linked Jira Issues',
      id: 'linkedJiraIssues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};

export const ValidationReference: Reference = {
  name: 'Verification Template Reference',
  typeName: 'VerificationTemplateReference',
  props: [
    {
      name: 'Product',
      id: 'product',
      type: Product
    },
    { name: 'Version', id: 'version', type: 'number' },
    {
      name: 'Components',
      id: 'components',
      type: ValidationComponents,
      isArray: true
    },
    { name: 'Total', id: 'total', type: 'number' },
    {
      name: 'Validation Without Components',
      id: 'validationWithoutComponents',
      type: ValidationWithoutComponents,
      isArray: true
    }
  ]
};

// Design Spec

export const DesignSpec: Reference = {
  name: 'Design Specification',
  typeName: 'DesignSpec',
  props: [
    { id: 'id', name: 'ID', type: 'string' },
    { id: 'title', name: 'Title', type: 'string' },
    { id: 'description', name: 'Description', type: 'string' }
  ]
};

// Traceability

export const TraceabilityTable: Reference = {
  name: 'Traceability Table',
  typeName: 'TraceabilityTable',
  props: [
    { name: 'User Need ID', id: 'unId', type: 'string' },
    { name: 'User Need Title', id: 'unTitle', type: 'string' },
    { name: 'User Need Status', id: 'unStatus', type: 'string' },
    {
      id: 'unJiraIssues',
      name: 'User Need Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { name: 'Design Input ID', id: 'inId', type: 'string' },
    { name: 'Design Input Title', id: 'inTitle', type: 'string' },
    { name: 'Design Input Status', id: 'inStatus', type: 'string' },
    {
      id: 'inJiraIssues',
      name: 'Design Input Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    {
      name: 'Design Input Essential Requirements',
      id: 'inEssentialRequirements',
      type: 'string'
    },
    {
      name: 'Design Input Verification Method',
      id: 'inVerificationMethod',
      type: 'string'
    },
    {
      name: 'Design Input When to Verify',
      id: 'inWhenToVerify',
      type: 'string'
    },
    { name: 'Design Output ID', id: 'outId', type: 'string' },
    { name: 'Design Output Title', id: 'outTitle', type: 'string' },
    { name: 'Design Output Status', id: 'outStatus', type: 'string' },
    {
      id: 'outJiraIssues',
      name: 'Design Output Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    {
      name: 'Design Output Essential Requirements',
      id: 'outEssentialRequirements',
      type: 'string'
    },
    { name: 'Design Verification ID', id: 'verId', type: 'string' },
    { name: 'Design Verification Title', id: 'verTitle', type: 'string' },
    { name: 'Design Verification Status', id: 'verStatus', type: 'string' },
    {
      id: 'verJiraIssues',
      name: 'Design Verification Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { name: 'Design Validation ID', id: 'valId', type: 'string' },
    { name: 'Design Validation Title', id: 'valTitle', type: 'string' },
    { name: 'Design Validation Status', id: 'valStatus', type: 'string' },
    {
      id: 'valJiraIssues',
      name: 'Design Validation Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    },
    { name: 'Design Specification', id: 'ds', type: DesignSpec },
    { name: 'Design Spec ID', id: 'dsId', type: 'string' },
    { name: 'Design Spec Title', id: 'dsTitle', type: 'string' },
    { name: 'Design Spec Status', id: 'dsStatus', type: 'string' },
    {
      id: 'dsJiraIssues',
      name: 'Design Spec Linked Jira Issues',
      type: LinkedJiraIssues,
      isArray: true
    }
  ]
};

export const TraceabilityReportReference: Reference = {
  name: 'Traceability Report Template Reference',
  typeName: 'TraceabilityReportTemplateReference',
  props: [
    { name: 'Product', id: 'product', type: Product },
    { name: 'Version', id: 'version', type: 'string' },
    { name: 'Table', id: 'table', type: TraceabilityTable, isArray: true }
  ]
};

// eClinical

const eClinicalUserNeed: Reference = {
  name: 'User Need',
  typeName: 'UserNeed',
  props: [
    { name: 'ID', id: 'id', type: 'string' },
    { name: 'Title', id: 'title', type: 'string' }
  ]
};

const eClinicalUserStories: Reference = {
  name: 'User Stories',
  typeName: 'UserStories',
  props: [
    { name: 'User Need ID', id: 'userNeedId', type: 'string' },
    { name: 'Design Input ID', id: 'designInputId', type: 'string' },
    { name: 'Issue Key', id: 'issueKey', type: 'string' },
    { name: 'Summary', id: 'summary', type: 'string' }
  ]
};

const eClinicalRequirements: Reference = {
  name: 'Requirements',
  typeName: 'Requirements',
  props: [
    { name: 'User Need ID', id: 'userNeedId', type: 'string' },
    { name: 'Design Input ID', id: 'designInputId', type: 'string' },
    { name: 'Title', id: 'title', type: 'string' }
  ]
};

const eClinicalSubTasks: Reference = {
  name: 'Sub Tasks',
  typeName: 'SubTasks',
  props: [
    { name: 'Key', id: 'key', type: 'number' },
    { name: 'Summary', id: 'summary', type: 'string' },
    { name: 'Description', id: 'description', type: 'string' }
  ]
};

const eClinicalRequirementsWithSubTasks: Reference = {
  name: 'Requirements With SubTasks',
  typeName: 'RequirementsWithSubTasks',
  props: [
    { name: 'User Need ID', id: 'userNeedId', type: 'string' },
    { name: 'Design Input ID', id: 'designInputId', type: 'string' },
    { name: 'Issue Key', id: 'issueKey', type: 'string' },
    { name: 'Summary', id: 'summary', type: 'string' },
    {
      name: 'Sub Tasks',
      id: 'subTasks',
      type: eClinicalSubTasks,
      isArray: true
    }
  ]
};

export const eClinicalWorksReportReference: Reference = {
  name: 'eClinical Works Report Template Reference',
  typeName: 'eClinicalWorksReportTemplateReference',
  props: [
    {
      name: 'UserNeeds',
      id: 'userNeeds',
      type: eClinicalUserNeed,
      isArray: true
    },
    {
      name: 'UserStories',
      id: 'userStories',
      type: eClinicalUserStories,
      isArray: true
    },
    {
      name: 'Requirements',
      id: 'requirements',
      type: eClinicalRequirements,
      isArray: true
    },
    {
      name: 'Requirements With SubTasks',
      id: 'requirementsWithSubTasks',
      type: eClinicalRequirementsWithSubTasks,
      isArray: true
    }
  ]
};

// Hazard

const HazardUserNeed: Reference = {
  ...UserNeed,
  props: [
    ...UserNeed.props,
    { id: 'status', name: 'User Need Status', type: 'string' }
  ]
};

const HazardDesignInput: Reference = {
  ...DesignInput,
  props: [
    ...DesignInput.props,
    { id: 'status', name: 'Design Input Status', type: 'string' }
  ]
};

const Hazard: Reference = {
  name: 'Hazard',
  typeName: 'Hazard',
  props: [
    { id: 'id', name: 'ID', type: 'string' },
    { id: 'title', name: 'Title', type: 'string' },
    { id: 'category', name: 'Category', type: 'string' },
    { id: 'otherCategory', name: 'Other Category', type: 'string' },
    { id: 'description', name: 'Description', type: 'string' },
    { id: 'status', name: 'Status', type: 'string' },
    { id: 'impact', name: 'Impact', type: 'string' },
    { id: 'version', name: 'Version', type: 'string' },
    { id: 'initialSeverity', name: 'Initial Severity', type: 'string' },
    { id: 'initialProbability', name: 'Initial Probability', type: 'string' },
    { id: 'initialRisk', name: 'Initial Risk', type: 'string' },
    { id: 'residualSeverity', name: 'Residual Severity', type: 'string' },
    { id: 'residualProbability', name: 'Residual Probability', type: 'string' },
    { id: 'residualRisk', name: 'Residual Risk', type: 'string' },
    { id: 'riskJustification', name: 'Risk Justification', type: 'string' },
    {
      id: 'benefitRiskAnalysis',
      name: 'Benefit Risk Analysis',
      type: 'string'
    },
    { id: 'comments', name: 'Comments', type: 'string' },
    { id: 'hazardOwner', name: 'Hazard Owner', type: User },
    { id: 'techLead', name: 'Tech Lead', type: User },
    {
      id: 'userNeeds',
      name: 'Linked User Needs',
      type: HazardUserNeed,
      isArray: true
    },
    {
      id: 'designInputs',
      name: 'Linked Design Inputs',
      type: HazardDesignInput,
      isArray: true
    }
  ]
};

const BaseHazardEntityProps: ReferenceProperty[] = [
  { id: 'id', name: 'ID', type: 'string' },
  { id: 'title', name: 'Title', type: 'string' },
  { id: 'description', name: 'Description', type: 'string' },
  { id: 'status', name: 'Status', type: 'string' }
];

const HazardCause: Reference = {
  name: 'Hazard Cause',
  typeName: 'HazardCause',
  props: [
    ...BaseHazardEntityProps,
    { id: 'initialHarm', name: 'Initial Harm', type: 'string' },
    { id: 'initialRisk', name: 'Initial Risk', type: 'string' },
    { id: 'residualHarm', name: 'Residual Harm', type: 'string' },
    { id: 'residualRisk', name: 'Residual Risk', type: 'string' }
  ]
};

const HazardControl: Reference = {
  name: 'Hazard Control',
  typeName: 'HazardControl',
  props: [
    ...BaseHazardEntityProps,
    { id: 'controlVerification', name: 'Control Verification', type: 'string' }
  ]
};

const HazardAction: Reference = {
  name: 'Hazard Action',
  typeName: 'HazardAction',
  props: BaseHazardEntityProps
};

const HazardTable: Reference = {
  name: 'Hazard Table',
  typeName: 'HazardTable',
  props: [
    { name: 'Hazard', id: 'hazard', type: Hazard },
    { name: 'Hazard Cause', id: 'cause', type: HazardCause },
    { name: 'Hazard Control', id: 'control', type: HazardControl },
    { name: 'Hazard Action', id: 'action', type: HazardAction },
    {
      id: 'userNeeds',
      name: 'Linked Hazard User Needs',
      type: HazardUserNeed,
      isArray: true
    },
    {
      id: 'designInputs',
      name: 'Linked Hazard Design Inputs',
      type: HazardDesignInput,
      isArray: true
    }
  ]
};

export const HazardReference: Reference = {
  name: 'Hazard Template Reference',
  typeName: 'HazardTemplateReference',
  props: [
    { id: 'table', name: 'Hazard Table', type: HazardTable, isArray: true },
    { id: 'hazards', name: 'Hazards', type: Hazard, isArray: true }
  ]
};
